import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import cls from 'classnames';

// helpers
import { getContent, isDDEnv, isWHEnv } from '@/utils/helper';
import {
  analytics,
  getClickCtaDetail,
  getClickExternalCtaDetail,
} from '@/utils/analytics';
// ui
import Pill from '@/atoms/Pill/Pill';
import Text from '@/atoms/Text/Text';
import CtfCta, { CtaWrapper } from '@/molecules/CTA/index';
import { DynamicAsset } from '@/molecules/Media/index';
import BlockText from '@/molecules/BlockText/BlockText';

// constants
import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';
import { CtfCtaStyle, CtfWidthOptions } from '@/utils/constants';

function Card({ assets = [], entries = [], fields = {}, isBlock = false, cardImageClassName, contentCta, isFromContentColumn, isFromTwoColumnContent, hasBackground, isPinnedContent }) {
  const router = useRouter();

  // @TODO: Improve variable spreading and type checking.
  const {
    dynamicAsset,
    primaryCTA,
    secondaryCTA,
    tags,
    title,
    titleHeadingSize,
    text,
    Width: width,
    cloudinaryAssets,
  } = fields;
  const tagEntries = tags && tags.length > 0
    ? tags.map(t => getContent(t.sys.id, entries)).filter(t => t && t.fields.featured).slice(0, 3)
    : [];

  const showQuote = !tagEntries.length && !dynamicAsset && !cloudinaryAssets && !primaryCTA && !secondaryCTA;
  const getCtaAnalytics = (e, data) => {
    analytics({
      window,
      document,
      detail: data?.localUrl
        ? getClickCtaDetail(e, data)
        : getClickExternalCtaDetail(e, data),
    });
  };

  const blockTextContent = getContent(text?.sys?.id, entries);
  const isDD = isDDEnv();
  const isWH = isWHEnv();
  const titleSize = isDD ? 'body-large-bold' : titleHeadingSize ?? 'default';

  return (
    <div
      className={`aaco-card w-full relative ${
        showQuote && 'flex flex-col justify-center'
      } ${isBlock && 'container mx-auto py-12'} ${
        isBlock &&
        width === CtfWidthOptions.FULL_WIDTH &&
        'md:pl-0 md:pr-0 md:mx-0'
      } ${isBlock && width === CtfWidthOptions.NO_LEFT && 'md:pl-0 no-side'} ${
        isBlock && width === CtfWidthOptions.NO_RIGHT && 'md:pr-0 no-side'
      } ${
        cls({
          'flex flex-col justify-between h-full': isWH
        })
      }`}
    >
      <div className="relative min-h-[20px]">
        {!showQuote && (cloudinaryAssets || dynamicAsset) && (
          <div
            className={`aaco-card__image mb-[15px] ${
              cardImageClassName || isBlock || 'h-auto'
            } ${isFromTwoColumnContent ? 'md:h-[450px] ' : ''} ${
              isWH ? 'border-solid border-black' : ''
            }`}
          >
          {(cloudinaryAssets || dynamicAsset) && (<DynamicAsset
              assets={assets}
              callToAction={primaryCTA}
              entries={entries}
              field={cloudinaryAssets ?? dynamicAsset}
              container={isBlock ? (width === CtfWidthOptions.PADDING ? "container" : "full") : "grid-card"}
              title={title}
                style={`aspect-[4/5] ${
                  isFromContentColumn
                    ? 'dd-en:rounded-[6px] dd-kr:rounded-[6px]'
                    : 'dd-en:rounded-[8px] dd-kr:rounded-[8px]'
                } dd-kr:aspect-[3/4] dd-en:aspect-[3/4] dd-kr:h-auto dd-en:h-auto`}
            />)}
          </div>
        )}
        {tagEntries && (
          <div className="pill-wrapper absolute bottom-8 dd-kr:bottom-[15px] dd-en:bottom-[15px] left-[15px] dd-en:md:left-5 dd-kr:dd-en:md:left-5 z-10 inline-flex items-center flex-wrap gap-2">
            {isPinnedContent && (
              <Pill
                tag="pill"
                style="pill-action dd-en:px-[15px] dd-en:py-[4px] dd-kr:px-[15px] dd-kr:py-[4px]"
                small
                inlineStyle={{ backgroundColor: '#e5f76f'}}
              >
                Featured
              </Pill>
            )}
            {tagEntries.map((tag, i) => (
              <Pill
                key={tag.sys.id}
                tag="pill"
                style="pill-action dd-en:px-[15px] dd-en:py-[4px] dd-kr:px-[15px] dd-kr:py-[4px]"
                onClick={() => router.push(`/search?t=${tag?.fields?.slug}`)}
                small
              >
                {tag.fields?.name}
              </Pill>
            ))}
          </div>
        )}
      </div>
      <div className={
        cls({
          'aaco-card__content pt-3 pb-4': true && !showQuote,
          'grow flex flex-col border-y border-black': isWH,
          'justify-start': showQuote && isWH,
          'justify-center': isWH && !showQuote,
        })
      }>
        {!showQuote && (
          <>
            {!primaryCTA || isDD ? (
              <Text tag={titleSize} style={`aaco-card__title ${hasBackground ? 'dd-kr:text-white dd-en:text-white' : 'text-black dd-kr:text-progressive-blue dd-en:text-progressive-blue'}`}>
                {title}
              </Text>
            ) : (
              <CtaWrapper page={primaryCTA} onClick={getCtaAnalytics}>
                <Text tag={titleSize} style={`aaco-card__title ${hasBackground ? 'text-acacia dd-kr:text-white dd-en:text-white' : 'text-black dd-kr:text-progressive-blue dd-en:text-progressive-blue'} ${isWH ? 'uppercase !tracking-[0.07em] text-[13px]' : ''}`}>
                  {title}
                </Text>
              </CtaWrapper>
            )}
          </>
        )}
        {blockTextContent?.length > 0 && (
          <BlockText
            blockTextStyle="gap-y-4"
            blockWrapperStyle="py-4 px-0 md:px-0"
            fields={blockTextContent}
            richTextStyle={{
              paragraph: {
                customFont: TEXT_BASE_CLASS['body'],
              },
            }}
            entries={entries}
            invert
          />
        )}
        {text && !text.sys && (
          <p
            className={`font-text ${
              showQuote
                ? 'font-heading font-h4 text-h4 text-black'
                : 'text-grey-5 dd-kr:text-body-text-grey dd-en:text-body-text-grey'
            } dd-kr:font-body dd-kr:text-body dd-en:font-body dd-en:text-body`}
          >
            {text}
          </p>
        )}
        {contentCta && (
          <div className="aaco-card__cta mt-2">
            <CtaWrapper
              url={primaryCTA ?? secondaryCTA}
              onClick={getCtaAnalytics}
            >
              <Text
                tag="button"
                style={cls({
                  'underline underline-offset-[5px] decoration-[1.5px] decoration-acacia text-black dd-en:decoration-actual-dd-gold dd-kr:decoration-actual-dd-gold dd-kr:text-progressive-blue dd-en:text-progressive-blue': !isWH,
                  'aaco-cta__primary-wh': isWH,
                })}
              >
                {contentCta?.name}
              </Text>
            </CtaWrapper>
          </div>
        )}
        {primaryCTA && isDD && (
          <div className="aaco-card__cta mt-2">
            <CtfCta
              field={primaryCTA}
              entries={entries}
              type={CtfCtaStyle.DD_SECONDARY}
              onClick={getCtaAnalytics}
            />
          </div>
        )}
        {secondaryCTA && (
          <div className="aaco-card__cta mt-2">
            <CtfCta
              field={secondaryCTA}
              entries={entries}
              type={isDDEnv() ? CtfCtaStyle.DD_SECONDARY : CtfCtaStyle.PRIMARY}
              onClick={getCtaAnalytics}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// @TODO: define real props for the Story based on it requirements
Card.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
  cardImageClassName: PropTypes.string,
};

export default Card;
